import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Trash, ArrowCounterClockwise, Warning, MagnifyingGlass, Key, Buildings, Users,
  Calendar, Trophy, Sword, X, Copy, Broom,
} from "@phosphor-icons/react";

const KIND_LABELS = {
  users: "Usuarios",
  retas: "Retas",
  tournaments: "Torneos",
  clubs: "Clubes",
  rentals: "Rentas",
  matches: "Partidos",
};
const KIND_ICONS = {
  users: Users,
  retas: Sword,
  tournaments: Trophy,
  clubs: Buildings,
  rentals: Calendar,
  matches: Calendar,
};

export default function Recuperacion() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orphans");

  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="recuperacion-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-['Barlow_Condensed'] font-black uppercase tracking-tight text-white">
          Recuperación
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Papelera de reciclaje, datos huérfanos y recuperación de acceso. Solo admin.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="orphans" data-testid="tab-orphans"><Warning size={14} className="mr-1" /> Huérfanos</TabsTrigger>
          <TabsTrigger value="trash" data-testid="tab-trash"><Trash size={14} className="mr-1" /> Papelera</TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access"><Key size={14} className="mr-1" /> Acceso</TabsTrigger>
        </TabsList>
        <TabsContent value="orphans" className="mt-4"><OrphansTab /></TabsContent>
        <TabsContent value="trash" className="mt-4"><TrashTab /></TabsContent>
        <TabsContent value="access" className="mt-4"><AccessTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ───────── Orphans ─────────

function OrphansTab() {
  const [data, setData] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/recovery/orphans"),
        api.get("/clubs"),
      ]);
      setData(r1.data);
      setClubs(Array.isArray(r2.data) ? r2.data : (r2.data?.items || []));
    } catch (e) {
      toast.error("Error al cargar huérfanos");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-gray-400" data-testid="orphans-loading">Cargando…</div>;
  if (!data) return null;

  const filterFn = (u) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (u.name || "").toLowerCase().includes(f)
      || (u.email || "").toLowerCase().includes(f)
      || (u.phone || "").toLowerCase().includes(f);
  };

  const orphanedUsers = (data.users_without_club || []).filter(filterFn);

  return (
    <div className="space-y-6" data-testid="orphans-content">
      <div className="grid sm:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Usuarios sin club" value={data.users_without_club?.length || 0} />
        <KpiCard icon={Sword} label="Retas vacías" value={data.retas_empty?.length || 0} />
        <KpiCard icon={Trophy} label="Torneos vacíos" value={data.tournaments_empty?.length || 0} />
        <KpiCard icon={Calendar} label="Partidos huérfanos" value={data.matches_unlinked?.length || 0} />
      </div>

      <div className="bg-black/40 border border-white/10 p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">
            Usuarios sin club asignado · {orphanedUsers.length}
          </h3>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Input
              placeholder="Filtrar por nombre, email o teléfono…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-black/50 border-white/10 max-w-xs"
              data-testid="orphan-filter"
            />
            <Button variant="outline" size="sm" onClick={load} data-testid="orphans-refresh"><ArrowCounterClockwise size={14} /></Button>
          </div>
        </div>

        <div className="space-y-2 max-h-[600px] overflow-auto">
          {orphanedUsers.length === 0 && (
            <div className="text-xs text-gray-500 py-4">No hay usuarios huérfanos con ese filtro.</div>
          )}
          {orphanedUsers.map((u) => (
            <OrphanUserRow key={u.id} u={u} clubs={clubs} onChanged={load} />
          ))}
        </div>
      </div>

      {(data.retas_empty?.length || 0) > 0 && (
        <DataSection
          title={`Retas sin participantes (${data.retas_empty.length})`}
          items={data.retas_empty}
          cols={[
            { key: "name", label: "Nombre" },
            { key: "club_name", label: "Club" },
            { key: "date", label: "Fecha" },
            { key: "status", label: "Estado" },
          ]}
        />
      )}
      {(data.tournaments_empty?.length || 0) > 0 && (
        <DataSection
          title={`Torneos sin participantes (${data.tournaments_empty.length})`}
          items={data.tournaments_empty}
          cols={[
            { key: "name", label: "Nombre" },
            { key: "club_name", label: "Club" },
            { key: "start_date", label: "Fecha inicio" },
            { key: "status", label: "Estado" },
          ]}
        />
      )}
      {(data.matches_unlinked?.length || 0) > 0 && (
        <DataSection
          title={`Partidos sin vincular (${data.matches_unlinked.length})`}
          items={data.matches_unlinked.map(m => ({
            ...m,
            pair1_name: ((m.pair1 || {}).p1_name || "") + " / " + ((m.pair1 || {}).p2_name || ""),
            pair2_name: ((m.pair2 || {}).p1_name || "") + " / " + ((m.pair2 || {}).p2_name || ""),
          }))}
          cols={[
            { key: "date", label: "Fecha" },
            { key: "pair1_name", label: "Pareja 1" },
            { key: "pair2_name", label: "Pareja 2" },
            { key: "category", label: "Cat" },
            { key: "status", label: "Estado" },
          ]}
        />
      )}
    </div>
  );
}

function OrphanUserRow({ u, clubs, onChanged }) {
  const [clubId, setClubId] = useState("");
  const [saving, setSaving] = useState(false);
  const assign = async () => {
    if (!clubId) return toast.error("Selecciona un club");
    setSaving(true);
    try {
      const r = await api.post("/admin/recovery/orphans/assign-club", { user_id: u.id, club_id: clubId });
      toast.success(`${u.name} asignado a ${r.data.club_name}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid sm:grid-cols-[1fr,260px,140px] gap-2 items-center bg-black/30 border border-white/10 p-3" data-testid={`orphan-user-${u.id}`}>
      <div>
        <div className="text-sm text-white font-medium">{u.name}</div>
        <div className="text-[10px] text-gray-400 font-mono">
          {u.email || u.phone || u.id} · <Badge variant="outline" className="ml-1 text-[9px]">{u.role}</Badge> {u.category ? `· Cat ${u.category}` : ""}
        </div>
      </div>
      <Select value={clubId} onValueChange={setClubId}>
        <SelectTrigger className="bg-black/50 border-white/10 h-9 text-xs" data-testid={`orphan-club-${u.id}`}>
          <SelectValue placeholder="Selecciona un club" />
        </SelectTrigger>
        <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-[300px]">
          {clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" disabled={!clubId || saving} onClick={assign} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80 h-9" data-testid={`orphan-assign-${u.id}`}>
        {saving ? "..." : "Asignar"}
      </Button>
    </div>
  );
}

function DataSection({ title, items, cols }) {
  return (
    <div className="bg-black/40 border border-white/10 p-5 space-y-3">
      <h3 className="text-base font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">{title}</h3>
      <div className="max-h-[300px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5">
            <tr>{cols.map(c => <th key={c.key} className="text-left text-gray-400 uppercase tracking-wider p-2">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {items.slice(0, 50).map((it, i) => (
              <tr key={it.id || i} className="border-t border-white/5">
                {cols.map(c => <td key={c.key} className="p-2 text-gray-200">{String(it[c.key] ?? "—").slice(0, 60)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {items.length > 50 && <div className="text-[10px] text-gray-500 mt-2">+{items.length - 50} más</div>}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-black/40 border border-white/10 p-4 flex items-center gap-3">
      <Icon size={28} className="text-[#CCFF00]" />
      <div>
        <div className="text-2xl font-['Barlow_Condensed'] font-black text-white">{value}</div>
        <div className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

// ───────── Trash ─────────

function TrashTab() {
  const [data, setData] = useState({ entries: [], counts_by_kind: {} });
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = kind === "all" ? "" : `?kind=${kind}`;
      const r = await api.get(`/admin/recovery/trash${qs}`);
      setData(r.data);
    } catch (e) { toast.error("Error al cargar papelera"); }
    finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const restore = async (tid) => {
    if (!window.confirm("¿Restaurar esta entrada al sistema?")) return;
    try {
      await api.post(`/admin/recovery/restore/${tid}`);
      toast.success("Restaurado correctamente");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const purge = async (tid) => {
    if (!window.confirm("⚠️ Esto eliminará permanentemente la entrada. ¿Continuar?")) return;
    try {
      await api.delete(`/admin/recovery/purge/${tid}`);
      toast.success("Purgado permanentemente");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const purgeExpired = async () => {
    if (!window.confirm("Purgar TODAS las entradas expiradas (>90 días). ¿Continuar?")) return;
    try {
      const r = await api.delete(`/admin/recovery/trash/empty?only_expired=true`);
      toast.success(`${r.data.deleted} entradas purgadas`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="trash-content">
      <div className="grid sm:grid-cols-6 gap-2 text-xs">
        {Object.entries(KIND_LABELS).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`p-3 border transition-all text-left ${kind === k ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}
            data-testid={`trash-filter-${k}`}
          >
            <div className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</div>
            <div className="text-xl font-['Barlow_Condensed'] font-black text-white">{data.counts_by_kind?.[k] || 0}</div>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button size="sm" variant={kind === "all" ? "default" : "outline"} onClick={() => setKind("all")} data-testid="trash-filter-all">Todos</Button>
          <Button size="sm" variant="outline" onClick={load} data-testid="trash-refresh"><ArrowCounterClockwise size={14} /></Button>
        </div>
        <Button size="sm" variant="outline" onClick={purgeExpired} className="text-red-400 border-red-500/30" data-testid="trash-purge-expired">
          <Broom size={14} className="mr-1" /> Purgar expirados (90+ días)
        </Button>
      </div>
      <div className="space-y-2 max-h-[600px] overflow-auto">
        {loading && <div className="text-gray-400 text-xs">Cargando…</div>}
        {!loading && data.entries.length === 0 && (
          <div className="bg-black/40 border border-white/10 p-6 text-center text-sm text-gray-500" data-testid="trash-empty">
            La papelera está vacía. ✨
          </div>
        )}
        {data.entries.map((e) => {
          const Icon = KIND_ICONS[e.kind] || Trash;
          return (
            <div key={e.id} className="bg-black/40 border border-white/10 p-3 flex items-center gap-3" data-testid={`trash-entry-${e.id}`}>
              <Icon size={20} className="text-[#CCFF00] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium truncate">{e.doc_name}</span>
                  <Badge variant="outline" className="text-[10px]">{KIND_LABELS[e.kind] || e.kind}</Badge>
                </div>
                <div className="text-[10px] text-gray-400 truncate">{e.doc_summary}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  Eliminado {e.deleted_at?.slice(0, 16).replace("T", " ")} por <span className="text-gray-300">{e.deleted_by_name || "—"}</span> · Expira {e.expires_at?.slice(0, 10)}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => restore(e.id)} className="text-[#CCFF00] border-[#CCFF00]/30 hover:bg-[#CCFF00]/10" data-testid={`trash-restore-${e.id}`}>
                <ArrowCounterClockwise size={14} className="mr-1" /> Restaurar
              </Button>
              <Button size="sm" variant="outline" onClick={() => purge(e.id)} className="text-red-400 border-red-500/30" data-testid={`trash-purge-${e.id}`}>
                <X size={14} />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────── Access (Reset password / Reactivate) ─────────

function AccessTab() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ live: [], in_trash: [] });
  const [searching, setSearching] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [working, setWorking] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await api.get(`/admin/recovery/users/search?q=${encodeURIComponent(q)}`);
      setResults(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSearching(false); }
  };

  const onResetConfirm = async () => {
    if (!resetTarget) return;
    setWorking(true); setResetResult(null);
    try {
      const r = await api.post(`/admin/recovery/users/${resetTarget.id}/reset-password`);
      setResetResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); setResetTarget(null); }
    finally { setWorking(false); }
  };

  const reactivate = async (uid) => {
    try {
      await api.post(`/admin/recovery/users/${uid}/reactivate`);
      toast.success("Usuario reactivado");
      search();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const restoreTrash = async (tid) => {
    try {
      await api.post(`/admin/recovery/restore/${tid}`);
      toast.success("Restaurado");
      search();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="access-content">
      <div className="bg-black/40 border border-white/10 p-5 space-y-3">
        <Label className="text-xs uppercase tracking-wider text-gray-400">Buscar usuario por nombre, email o teléfono</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Ej. Carlos Cortázar, cgonzcortazar@gmail.com o 464…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") search(); }}
            className="bg-black/50 border-white/10"
            data-testid="access-search-input"
          />
          <Button onClick={search} disabled={searching || !q.trim()} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="access-search-btn">
            <MagnifyingGlass size={16} className="mr-1" /> {searching ? "..." : "Buscar"}
          </Button>
        </div>
      </div>

      {(results.live.length > 0 || results.in_trash.length > 0) && (
        <div className="space-y-4">
          {results.live.length > 0 && (
            <div className="bg-black/40 border border-white/10 p-4 space-y-2">
              <h3 className="text-sm font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">
                Cuentas activas · {results.live.length}
              </h3>
              {results.live.map((u) => (
                <div key={u.id} className="bg-black/30 border border-white/10 p-3 flex items-center justify-between gap-3" data-testid={`access-live-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white font-medium">{u.name}</div>
                    <div className="text-[10px] text-gray-400 font-mono">
                      {u.email || u.phone} · <Badge variant="outline" className="text-[9px]">{u.role}</Badge>
                      {u.club_name ? <span className="ml-1">· {u.club_name}</span> : <span className="ml-1 text-amber-400">· sin club</span>}
                      {u.is_active === false && <span className="ml-1 text-red-400">· desactivado</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.is_active === false && (
                      <Button size="sm" variant="outline" onClick={() => reactivate(u.id)} className="text-green-400 border-green-500/30" data-testid={`access-reactivate-${u.id}`}>
                        Reactivar
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => { setResetTarget(u); setResetResult(null); }} className="text-[#CCFF00] border-[#CCFF00]/30" data-testid={`access-reset-${u.id}`}>
                      <Key size={14} className="mr-1" /> Restablecer contraseña
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {results.in_trash.length > 0 && (
            <div className="bg-black/40 border border-amber-500/30 p-4 space-y-2">
              <h3 className="text-sm font-['Barlow_Condensed'] font-black uppercase tracking-wider text-amber-300">
                ⚠ En la papelera · {results.in_trash.length}
              </h3>
              {results.in_trash.map((u) => (
                <div key={u.trash_id} className="bg-black/30 border border-amber-500/20 p-3 flex items-center justify-between gap-3" data-testid={`access-trash-${u.trash_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white font-medium">{u.name}</div>
                    <div className="text-[10px] text-gray-400 font-mono">
                      {u.email || u.phone} · {u.role} · Eliminado {u.deleted_at?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restoreTrash(u.trash_id)} className="text-[#CCFF00] border-[#CCFF00]/30" data-testid={`access-restore-${u.trash_id}`}>
                    <ArrowCounterClockwise size={14} className="mr-1" /> Restaurar cuenta
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {q && !searching && results.live.length === 0 && results.in_trash.length === 0 && (
        <div className="bg-black/40 border border-white/10 p-6 text-center text-sm text-gray-500">
          Sin resultados para "{q}".
        </div>
      )}

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetResult(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/15 text-white">
          <DialogHeader>
            <DialogTitle>Restablecer contraseña de {resetTarget?.name}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Se generará una contraseña temporal segura. El usuario debe cambiarla al iniciar sesión.
              <br /><span className="text-amber-400 text-xs">⚠ Esta contraseña se muestra UNA SOLA VEZ. Cópiala antes de cerrar.</span>
            </DialogDescription>
          </DialogHeader>
          {!resetResult ? (
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetTarget(null)}>Cancelar</Button>
              <Button onClick={onResetConfirm} disabled={working} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="reset-confirm">
                {working ? "Generando..." : "Generar contraseña"}
              </Button>
            </DialogFooter>
          ) : (
            <div className="space-y-3">
              <div className="bg-green-500/10 border border-green-500/30 p-4">
                <div className="text-xs uppercase tracking-wider text-green-300 mb-1">Contraseña temporal</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-2xl font-mono text-white tracking-wider" data-testid="reset-password-display">{resetResult.temporary_password}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(resetResult.temporary_password); toast.success("Copiado"); }} data-testid="reset-copy">
                    <Copy size={14} className="mr-1" /> Copiar
                  </Button>
                </div>
              </div>
              <div className="text-xs text-gray-400">
                Email: <span className="text-white">{resetResult.email}</span><br />
                {resetResult.note}
              </div>
              <DialogFooter>
                <Button onClick={() => { setResetTarget(null); setResetResult(null); }} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80">Listo</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
